import * as mapboxgl from "mapbox-gl";
import {
  translateGeometry,
  moveVertex,
  insertVertex,
  removeVertex,
  midpointFeatureCollection,
  vertexFeatureCollection,
  centerFeatureOf,
  centerOf,
  mainFeatureOf,
  pathKey,
} from "./features";
import {
  centerSourceId,
  fillLayerId,
  hitLayerId,
  midpointsLayerId,
  topmostAnnotationIdAt,
  verticesLayerId,
} from "./layers";

const DRAG_THRESHOLD_PX = 3;

let activeDrag: { owner: symbol; cancel: () => void } | null = null;
let selectedVertex: {
  annotationId: number;
  path: number[];
  clear: () => void;
  handleKeyDown: (e: KeyboardEvent) => void;
  handleMapMouseDown: (e: mapboxgl.MapMouseEvent) => void;
} | null = null;
// Microtask window: a synchronous remount (geometry refresh re-attaches the
// same annotation) consumes preservedSelection during attach; a true destroy
// has no re-attach, so the microtask clears the leftover and a later unrelated
// attach can't inherit it.
let preservedSelection: { annotationId: number; path: number[] } | null = null;

let keydownListenerInstalled = false;
const mapsWithSelectionClear = new WeakSet<object>();

const installKeydownListener = () => {
  if (keydownListenerInstalled) return;
  keydownListenerInstalled = true;
  document.addEventListener("keydown", (e) => selectedVertex?.handleKeyDown(e));
};

const installMapMouseDown = (map: mapboxgl.Map) => {
  if (mapsWithSelectionClear.has(map)) return;
  mapsWithSelectionClear.add(map);
  map.on("mousedown", (e) => selectedVertex?.handleMapMouseDown(e));
};

type ShapeKind = "line" | "polygon";

type AttachShapeEditingOpts = {
  map: mapboxgl.Map;
  id: number;
  sourceId: string;
  kind: ShapeKind;
  geometry: GeoJSON.Geometry;
  label: string;
  canEdit: () => boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onDragStart: () => void;
  persist: (geometry: GeoJSON.Geometry, revert: () => void) => void;
  onLiveGeometry?: (geometry: GeoJSON.Geometry) => void;
};

export const attachShapeEditing = (opts: AttachShapeEditingOpts) => {
  const {
    map,
    id,
    sourceId,
    kind,
    geometry,
    label,
    canEdit,
    onHoverStart,
    onHoverEnd,
    onDragStart,
    persist,
    onLiveGeometry,
  } = opts;

  const owner = Symbol("shape-editor");
  let dragGeneration = 0;
  let liveRaf: number | null = null;
  let setSourcesRaf: number | null = null;
  let currentGeometry = geometry;
  let hovering = false;
  let hoveredVertexPath: number[] | null = null;
  const tooltip = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 12,
    className: "bp6-dark annotation-handle-tooltip",
  });

  const verticesLayer = verticesLayerId(sourceId);
  const midpointsLayer = midpointsLayerId(sourceId);
  const hitLayer = hitLayerId(sourceId);
  const fillLayer = fillLayerId(sourceId);

  const hoverLayers =
    kind === "line"
      ? [hitLayer, midpointsLayer, verticesLayer]
      : [fillLayer, hitLayer, midpointsLayer, verticesLayer];

  const grabLayers = kind === "line" ? [hitLayer] : [hitLayer, fillLayer];

  const existingHoverLayers = () =>
    hoverLayers.filter((layerId) => map.getLayer(layerId));

  const selectedPathForRender = () =>
    selectedVertex?.annotationId === id ? selectedVertex.path : null;

  const vertexData = (geo: GeoJSON.Geometry) =>
    vertexFeatureCollection(geo, selectedPathForRender(), hoveredVertexPath);

  const setHandleVisibility = ({
    vertices,
    midpoints,
  }: {
    vertices?: boolean;
    midpoints?: boolean;
  }) => {
    if (midpoints !== undefined && map.getLayer(midpointsLayer)) {
      map.setLayoutProperty(
        midpointsLayer,
        "visibility",
        midpoints ? "visible" : "none"
      );
      if (midpoints) map.moveLayer(midpointsLayer);
    }
    if (vertices !== undefined && map.getLayer(verticesLayer)) {
      map.setLayoutProperty(
        verticesLayer,
        "visibility",
        vertices ? "visible" : "none"
      );
      if (vertices) map.moveLayer(verticesLayer);
    }
  };

  const showVertices = () => {
    if (!map.getLayer(verticesLayer)) return;
    setHandleVisibility({ vertices: true, midpoints: true });
    map.getCanvas().style.cursor = "move";
  };

  const hideVertices = () => {
    setHandleVisibility({ vertices: false, midpoints: false });
    map.getCanvas().style.cursor = "";
  };

  const scheduleLiveGeometry = (geo: GeoJSON.Geometry) => {
    if (!onLiveGeometry) return;
    if (liveRaf !== null) cancelAnimationFrame(liveRaf);
    liveRaf = requestAnimationFrame(() => {
      liveRaf = null;
      onLiveGeometry(geo);
    });
  };

  const setSources = (
    geo: GeoJSON.Geometry,
    centerOverride?: GeoJSON.Feature
  ) => {
    const mainSource = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
    const verticesSource = map.getSource(
      verticesLayer
    ) as mapboxgl.GeoJSONSource;
    const midpointsSource = map.getSource(
      midpointsLayer
    ) as mapboxgl.GeoJSONSource;
    const centerSource = map.getSource(
      centerSourceId(sourceId)
    ) as mapboxgl.GeoJSONSource;

    if (mainSource) {
      mainSource.setData(mainFeatureOf(geo, id));
    }
    if (verticesSource) {
      verticesSource.setData(vertexData(geo));
    }
    if (midpointsSource) {
      midpointsSource.setData(midpointFeatureCollection(geo));
    }
    if (centerSource) {
      centerSource.setData(centerOverride ?? centerFeatureOf(geo, id, label));
    }
  };

  const scheduleSetSources = (
    geo: GeoJSON.Geometry,
    centerOverride?: GeoJSON.Feature
  ) => {
    if (setSourcesRaf !== null) cancelAnimationFrame(setSourcesRaf);
    setSourcesRaf = requestAnimationFrame(() => {
      setSourcesRaf = null;
      setSources(geo, centerOverride);
    });
  };

  const hideMidpoints = () => {
    setHandleVisibility({ midpoints: false });
    tooltip.remove();
    map.getCanvas().style.cursor = "";
  };

  const editableHandler =
    <E,>(handler: (e: E) => void) =>
    (e: E) => {
      if (!canEdit()) {
        clearSelection();
        return;
      }
      handler(e);
    };

  const clearSelection = () => {
    if (selectedVertex?.annotationId !== id) return;
    selectedVertex = null;
    setSources(currentGeometry);
    if (!hovering) hideVertices();
  };

  const scheduleRevert =
    (
      origGeo: GeoJSON.Geometry,
      generation: number,
      clearSelectionOnRevert = false
    ) =>
    () => {
      if (generation !== dragGeneration) return;
      currentGeometry = origGeo;
      if (clearSelectionOnRevert) clearSelection();
      setSources(origGeo);
      onLiveGeometry?.(origGeo);
    };

  const selectVertex = (path: number[]) => {
    const previous = selectedVertex;
    // Clear the other shape first so its clearSelection guard still matches.
    if (previous && previous.annotationId !== id) previous.clear();
    selectedVertex = {
      annotationId: id,
      path,
      clear: clearSelection,
      handleKeyDown: onKeyDown,
      handleMapMouseDown: onMapMouseDown,
    };
    hoveredVertexPath = hovering ? path : null;
    setSources(currentGeometry);
    setHandleVisibility({ vertices: true });
  };

  const setHoveredVertex = (path: number[] | null) => {
    if (pathKey(path) === pathKey(hoveredVertexPath)) {
      return;
    }
    hoveredVertexPath = path;
    const verticesSource = map.getSource(
      verticesLayer
    ) as mapboxgl.GeoJSONSource;
    if (verticesSource) {
      verticesSource.setData(vertexData(currentGeometry));
    }
  };

  const showHandleTooltip =
    (
      text:
        | string
        | ((e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => string),
      cursor: string
    ) =>
    (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (activeDrag || !canEdit() || !e.features?.length) {
        tooltip.remove();
        return;
      }
      const coordinate = (e.features[0].geometry as GeoJSON.Point)
        .coordinates as [number, number];
      const label = typeof text === "function" ? text(e) : text;

      tooltip.setLngLat(coordinate).setText(label).addTo(map);
      map.getCanvas().style.cursor = cursor;
    };

  const onVertexTooltipMove = showHandleTooltip((e) => {
    const hoveredPath = JSON.parse(e.features![0].properties.path);
    const selectedPath = selectedPathForRender();
    const isSelected =
      selectedPath != null && pathKey(selectedPath) === pathKey(hoveredPath);
    return isSelected ? "Press Delete to remove vertex" : "Drag to move vertex";
  }, "move");
  const onMidpointTooltipMove = showHandleTooltip("Add new vertex", "cell");
  const onHandleTooltipLeave = () => tooltip.remove();

  const hasHandleAt = (point: mapboxgl.Point) => {
    const handleLayers = [verticesLayer, midpointsLayer].filter((layerId) =>
      map.getLayer(layerId)
    );
    if (handleLayers.length === 0) return false;
    return (
      map.queryRenderedFeatures(point, { layers: handleLayers }).length > 0
    );
  };

  const startDrag = (
    startEvent: mapboxgl.MapMouseEvent,
    dragType: "translate" | "vertex" | "midpoint",
    path?: number[],
    midpointPosition?: [number, number]
  ) => {
    if (activeDrag) return;
    setHoveredVertex(null);
    tooltip.remove();

    const generation = ++dragGeneration;
    const startLngLat = startEvent.lngLat;
    const startPoint = startEvent.point;
    const origGeo = JSON.parse(JSON.stringify(currentGeometry));
    const committed = dragType === "midpoint";
    const baseGeo = committed
      ? insertVertex(origGeo, path!, midpointPosition!)
      : origGeo;
    const startCenter =
      dragType === "translate" ? (centerOf(baseGeo) as [number, number]) : null;
    let dragGeo = baseGeo;
    let moved = false;

    if (committed) {
      onDragStart();
      setSources(baseGeo);
    }

    const detach = () => {
      map.off("mousemove", onMove);
      map.off("mouseup", onUp);
      window.removeEventListener("mouseup", onUp);
      if (liveRaf !== null) {
        cancelAnimationFrame(liveRaf);
        liveRaf = null;
      }
      if (setSourcesRaf !== null) {
        cancelAnimationFrame(setSourcesRaf);
        setSourcesRaf = null;
      }
      activeDrag = null;
    };

    const onMove = (e: mapboxgl.MapMouseEvent) => {
      const dx = e.point.x - startPoint.x;
      const dy = e.point.y - startPoint.y;
      if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

      if (!moved) {
        moved = true;
        if (!committed) onDragStart();
      }

      if (dragType === "translate") {
        const dLng = e.lngLat.lng - startLngLat.lng;
        const dLat = e.lngLat.lat - startLngLat.lat;
        dragGeo = translateGeometry(baseGeo, dLng, dLat);
        scheduleSetSources(dragGeo, {
          type: "Feature",
          id,
          geometry: {
            type: "Point",
            coordinates: [startCenter![0] + dLng, startCenter![1] + dLat],
          },
          properties: { label },
        });
      } else {
        dragGeo = moveVertex(baseGeo, path!, [e.lngLat.lng, e.lngLat.lat]);
        scheduleSetSources(dragGeo);
      }

      scheduleLiveGeometry(dragGeo);
    };

    const onUp = () => {
      detach();

      if (!moved && !committed) return;

      currentGeometry = dragGeo;
      onLiveGeometry?.(dragGeo);
      if ((dragType === "vertex" || dragType === "midpoint") && path) {
        selectVertex(path);
      } else {
        setSources(dragGeo);
      }
      persist(dragGeo, scheduleRevert(origGeo, generation, committed));
    };

    const cancel = () => {
      detach();

      if (!moved && !committed) return;

      setSources(origGeo);
      onLiveGeometry?.(origGeo);
    };

    activeDrag = { owner, cancel };
    map.on("mousemove", onMove);
    map.on("mouseup", onUp);
    window.addEventListener("mouseup", onUp);
  };

  const performHoverLeave = () => {
    hovering = false;
    setHoveredVertex(null);
    tooltip.remove();
    if (selectedVertex?.annotationId === id) {
      hideMidpoints();
      if (map.getLayer(verticesLayer)) {
        map.setLayoutProperty(verticesLayer, "visibility", "visible");
      }
    } else {
      hideVertices();
    }
    onHoverEnd();
  };

  const onHoverMove = (e: mapboxgl.MapMouseEvent) => {
    if (activeDrag) return;
    if (topmostAnnotationIdAt(map, e.point) !== id) {
      if (hovering) performHoverLeave();
      return;
    }
    const wasHovering = hovering;
    hovering = true;
    if (!wasHovering) onHoverStart();
    if (!canEdit()) {
      clearSelection();
      hideVertices();
      return;
    }
    setHoveredVertex(selectedPathForRender());
    if (!wasHovering) showVertices();
  };

  const onHoverLeave = (e: mapboxgl.MapMouseEvent) => {
    if (activeDrag) return;
    if (
      map.queryRenderedFeatures(e.point, {
        layers: existingHoverLayers(),
      }).length > 0
    ) {
      return;
    }
    performHoverLeave();
  };

  const onGrabMouseDown = editableHandler(
    (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (activeDrag || e.originalEvent.button !== 0) return;
      if (hasHandleAt(e.point)) return;
      clearSelection();
      e.preventDefault();
      startDrag(e, "translate");
    }
  );

  const onVertexMouseDown = editableHandler(
    (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (activeDrag || e.originalEvent.button !== 0) return;
      if (!e.features?.length) return;
      e.preventDefault();
      const path = JSON.parse(e.features[0].properties.path);
      selectVertex(path);
      startDrag(e, "vertex", path);
    }
  );

  const onMidpointMouseDown = editableHandler(
    (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (activeDrag || e.originalEvent.button !== 0) return;
      if (!e.features?.length) return;
      e.preventDefault();
      clearSelection();
      const path = JSON.parse(e.features[0].properties.path);
      const coordinate = (e.features[0].geometry as GeoJSON.Point)
        .coordinates as [number, number];
      startDrag(e, "midpoint", path, coordinate);
    }
  );

  const onMapMouseDown = (e: mapboxgl.MapMouseEvent) => {
    if (selectedVertex?.annotationId !== id) return;
    editableHandler((e: mapboxgl.MapMouseEvent) => {
      if (
        map.queryRenderedFeatures(e.point, {
          layers: existingHoverLayers(),
        }).length === 0
      ) {
        clearSelection();
      }
    })(e);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (selectedVertex?.annotationId !== id) return;
    editableHandler((e: KeyboardEvent) => {
      if (activeDrag) return;

      const target = e.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable
      ) {
        return;
      }

      if (e.key === "Escape") {
        clearSelection();
        return;
      }
      if (e.key !== "Delete" && e.key !== "Backspace") return;

      const origGeo = currentGeometry;
      const nextGeo = removeVertex(currentGeometry, selectedVertex!.path);
      if (!nextGeo) return;

      e.preventDefault();
      const generation = ++dragGeneration;
      currentGeometry = nextGeo;
      setSources(nextGeo);
      onLiveGeometry?.(nextGeo);
      clearSelection();
      persist(nextGeo, scheduleRevert(origGeo, generation));
    })(e);
  };

  const hoverLayerArray = existingHoverLayers();
  if (hoverLayerArray.length > 0) {
    map.on("mousemove", hoverLayerArray, onHoverMove);
    map.on("mouseleave", hoverLayerArray, onHoverLeave);
  }

  for (const layerId of grabLayers) {
    if (map.getLayer(layerId)) {
      map.on("mousedown", layerId, onGrabMouseDown);
    }
  }
  if (map.getLayer(verticesLayer)) {
    map.on("mousedown", verticesLayer, onVertexMouseDown);
    map.on("mousemove", verticesLayer, onVertexTooltipMove);
    map.on("mouseleave", verticesLayer, onHandleTooltipLeave);
  }
  if (map.getLayer(midpointsLayer)) {
    map.on("mousedown", midpointsLayer, onMidpointMouseDown);
    map.on("mousemove", midpointsLayer, onMidpointTooltipMove);
    map.on("mouseleave", midpointsLayer, onHandleTooltipLeave);
  }
  installKeydownListener();
  installMapMouseDown(map);

  if (preservedSelection?.annotationId === id) {
    const path = preservedSelection.path;
    preservedSelection = null;
    selectVertex(path);
  }

  return {
    destroy: () => {
      if (activeDrag?.owner === owner) activeDrag.cancel();
      if (hoverLayerArray.length > 0) {
        map.off("mousemove", hoverLayerArray, onHoverMove);
        map.off("mouseleave", hoverLayerArray, onHoverLeave);
      }
      for (const layerId of grabLayers) {
        if (map.getLayer(layerId)) {
          map.off("mousedown", layerId, onGrabMouseDown);
        }
      }
      if (map.getLayer(verticesLayer)) {
        map.off("mousedown", verticesLayer, onVertexMouseDown);
        map.off("mousemove", verticesLayer, onVertexTooltipMove);
        map.off("mouseleave", verticesLayer, onHandleTooltipLeave);
      }
      if (map.getLayer(midpointsLayer)) {
        map.off("mousedown", midpointsLayer, onMidpointMouseDown);
        map.off("mousemove", midpointsLayer, onMidpointTooltipMove);
        map.off("mouseleave", midpointsLayer, onHandleTooltipLeave);
      }
      tooltip.remove();
      if (selectedVertex?.annotationId === id) {
        const path = selectedVertex.path;
        selectedVertex = null;
        preservedSelection = { annotationId: id, path };
        // See preservedSelection comment above: clear leftover after one
        // microtask unless a synchronous remount already consumed it.
        queueMicrotask(() => {
          if (
            preservedSelection?.annotationId === id &&
            preservedSelection.path === path
          ) {
            preservedSelection = null;
          }
        });
      }
      hideVertices();
      if (map.getLayer(verticesLayer)) {
        map.removeLayer(verticesLayer);
      }
      if (map.getLayer(midpointsLayer)) {
        map.removeLayer(midpointsLayer);
      }
      if (map.getSource(verticesLayer)) {
        map.removeSource(verticesLayer);
      }
      if (map.getSource(midpointsLayer)) {
        map.removeSource(midpointsLayer);
      }
    },
  };
};
